import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Filter, Clock, Package, CheckCircle2, History } from "lucide-react";

export const OrderManagementSkeleton = () => {
    return (
        <div className="min-h-screen bg-background animate-pulse">
            {/* Header Skeleton */}
            <header className="border-b bg-foreground text-background">
                <div className="container mx-auto px-6 py-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Skeleton className="h-8 w-64 mb-2 bg-muted/20" />
                            <Skeleton className="h-4 w-48 bg-muted/20" />
                        </div>
                        {/* No bulk action toolbar skeleton needed here as it only appears when selected */}
                    </div>
                    {/* Metrics Skeletons */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, i) => (
                            <Card key={i} className="bg-muted/10 border-muted/20">
                                <CardContent className="pt-4 pb-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Skeleton className="h-4 w-20 mb-1 bg-muted/20" />
                                            <Skeleton className="h-7 w-24 bg-muted/20" />
                                        </div>
                                        <Skeleton className="h-8 w-8 rounded-full bg-muted/20" />
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </header>

            {/* Main Content Skeleton */}
            <main className="container mx-auto px-6 py-8 space-y-6">
                {/* Filters Collapsible Skeleton */}
                <Card className="border-border shadow-lg">
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                <Filter className="h-5 w-5 text-muted-foreground" />
                                <Skeleton className="h-6 w-40" />
                            </CardTitle>
                            <Skeleton className="h-5 w-5" />
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-4">
                        <div className="grid gap-4 md:grid-cols-5">
                            {[...Array(4)].map((_, i) => ( // 3 selects + 1 popover for date
                                <Skeleton key={i} className="h-10 w-full" />
                            ))}
                            <Skeleton className="h-10 w-full col-span-2" /> {/* Client select */}
                        </div>
                        <Skeleton className="h-10 w-full" /> {/* Search input */}
                        <div className="flex justify-between items-center pt-2">
                            <Skeleton className="h-9 w-28" />
                            <Skeleton className="h-5 w-40" />
                        </div>
                    </CardContent>
                </Card>

                {/* Table Skeleton */}
                <Card className="border-border shadow-lg">
                    <CardContent className="p-0">
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        {[...Array(7)].map((_, i) => ( // Adjust number of columns as per actual table
                                            <TableHead key={i}><Skeleton className="h-4 w-full" /></TableHead>
                                        ))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {[...Array(5)].map((_, i) => ( // 5 rows of skeleton data
                                        <TableRow key={i}>
                                            {[...Array(7)].map((_, j) => ( // Adjust number of columns
                                                <TableCell key={j}><Skeleton className="h-5 w-full" /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        <div className="flex items-center justify-between p-4 border-t">
                            <Skeleton className="h-5 w-48" />
                            <div className="flex items-center space-x-6">
                                <Skeleton className="h-8 w-32" />
                                <Skeleton className="h-8 w-24" />
                                <div className="flex space-x-2">
                                    <Skeleton className="h-8 w-8" />
                                    <Skeleton className="h-8 w-8" />
                                    <Skeleton className="h-8 w-8" />
                                    <Skeleton className="h-8 w-8" />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
};
